import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const formatDateLabel = (value) => {
  if (typeof value !== "string") return value;
  if (!value.includes("-")) return value;
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  const [year, month, day] = parts;
  return `${day}-${month}-${year.slice(-2)}`;
};

const formatTimeSpent = (mins) => {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h} Hours ${m} Minutes`;
};

const TimeSpentCard = ({ overallTimeSpent = [] }) => {
  const currentSelectionData = useMemo(() => {
    return overallTimeSpent || [];
  }, [overallTimeSpent]);

  const labels = useMemo(
    () =>
      currentSelectionData.map((d) => formatDateLabel(d.date || d.day || "")),
    [currentSelectionData],
  );

  const dataPoints = useMemo(
    () => currentSelectionData.map((d) => (d.time_spent || 0) / 60),
    [currentSelectionData],
  );

  const totalMinutes = useMemo(
    () =>
      currentSelectionData.reduce((acc, curr) => {
        const seconds = curr.time_spent || 0;
        return acc + seconds / 60;
      }, 0),
    [currentSelectionData],
  );

  const timeSpentChartData = useMemo(
    () => ({
      labels: labels.length > 0 ? labels : ["No Data"],
      datasets: [
        {
          label: "Minutes Spent",
          data: dataPoints.length > 0 ? dataPoints : [0],
          fill: true,
          borderColor: "#ffb300",
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return "rgba(255, 179, 0, 0.2)";
            const gradient = ctx.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom,
            );
            gradient.addColorStop(0, "rgba(255, 179, 0, 0.4)");
            gradient.addColorStop(1, "rgba(255, 179, 0, 0)");
            return gradient;
          },
          borderWidth: 3,
          tension: 0.4,
          pointBackgroundColor: "#ffb300",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointHitRadius: 10,
        },
      ],
    }),
    [dataPoints, labels],
  );

  const timeSpentChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: "index",
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(33, 43, 54, 0.9)",
          padding: 12,
          bodyFont: { size: 13, weight: "600" },
          titleFont: { size: 12 },
          displayColors: false,
          callbacks: {
            label: (context) => `${Math.round(context.parsed.y)} mins`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: "rgba(145, 158, 171, 0.1)", drawBorder: false },
          ticks: { color: "#919EAB", font: { size: 10 } },
        },
        x: {
          grid: { display: false },
          ticks: { color: "#919EAB", font: { size: 10 } },
        },
      },
    }),
    [],
  );

  return (
    <div className="card-styled-wrapper time-spent-card">
      <div className="time-spent-card__content">
        <div className="time-spent-header">
          <p className="time-spent-title">Total Time Spent (mins)</p>
        </div>

        <div className="time-spent-top-row">
          <div className="time-spent-summary">
            <p className="time-spent-summary-label">Total time spent</p>
            <p className="time-spent-summary-value">
              {formatTimeSpent(totalMinutes)}
            </p>
          </div>
        </div>

        <div style={{ display: "flex", height: 300, width: "100%" }}>
          <div
            style={{
              width: 50,
              flexShrink: 0,
              position: "relative",
              zIndex: 1,
              backgroundColor: "#fff",
            }}
          >
            <Line
              data={{
                labels: labels,
                datasets: [{ data: [], label: "" }],
              }}
              options={{
                ...timeSpentChartOptions,
                plugins: {
                  legend: { display: false },
                  tooltip: { enabled: false },
                },
                scales: {
                  ...timeSpentChartOptions.scales,
                  x: {
                    ...timeSpentChartOptions.scales.x,
                    ticks: { color: "transparent" },
                    grid: { display: false, drawBorder: false },
                  },
                  y: {
                    ...timeSpentChartOptions.scales.y,
                    max: Math.ceil(Math.max(...dataPoints, 0) * 1.1) || 10,
                    afterFit: (axis) => {
                      axis.width = 50;
                    },
                  },
                },
                maintainAspectRatio: false,
              }}
            />
          </div>

          <div
            className="time-spent-chart-container custom-scrollbar"
            style={{
              overflowX: "auto",
              flexGrow: 1,
              width: "calc(100% - 50px)",
            }}
          >
            <div
              className="time-spent-chart"
              style={{
                width: `${Math.max(labels.length * 60, 600)}px`,
              }}
            >
              <Line
                data={timeSpentChartData}
                options={{
                  ...timeSpentChartOptions,
                  scales: {
                    ...timeSpentChartOptions.scales,
                    y: {
                      ...timeSpentChartOptions.scales.y,
                      max: Math.ceil(Math.max(...dataPoints, 0) * 1.1) || 10,
                      grid: {
                        color: "rgba(145, 158, 171, 0.1)",
                        drawBorder: false,
                        drawTicks: false,
                      },
                      ticks: { display: false },
                      border: { display: false },
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSpentCard;
